import {
  FC,
  FocusEvent,
  PointerEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  DEFAULT_PROMO_SHOW_DELAY_MS,
  getDisplayDurationMs,
  normalizePromoPath,
  PromoCampaign,
} from "./campaigns";
import {
  getPendingPromoCampaign,
  getPromoInitialPathname,
  isPromoConsumed,
  markPromoConsumed,
} from "./promoSession";
import { trackPromoEvent } from "./analytics";
import "./promo.css";

type PromoCloseReason = "close_button" | "not_now" | "scrim" | "escape" | "timer" | "cta" | "route_change";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

type PromoModalProps = {
  /** False while the app shell is still loading; the promo waits for the first true. */
  isReady?: boolean;
};

/**
 * Opens the active promo once per full page load when the landing path is eligible.
 * In-app navigation never opens it, and leaving the page while it is open closes it for good.
 */
const PromoModal: FC<PromoModalProps> = ({ isReady = true }) => {
  const { pathname } = useLocation();
  const [campaign, setCampaign] = useState<PromoCampaign | null>(null);
  const campaignRef = useRef<PromoCampaign | null>(null);

  const handleClose = useCallback((reason: PromoCloseReason) => {
    const current = campaignRef.current;
    if (!current) return;
    campaignRef.current = null;
    setCampaign(null);
    trackPromoEvent(reason === "cta" ? "promo_cta_click" : "promo_dismiss", {
      promo_id: current.id,
      reason,
    });
  }, []);

  useEffect(() => {
    const current = normalizePromoPath(pathname);
    const initial = normalizePromoPath(getPromoInitialPathname());
    // Trailing slashes and glued query strings are the same landing route.
    if (current === initial) return;
    // A real route change before the dialog opens cancels this page load.
    // Once it is visible, the same change closes it.
    markPromoConsumed();
    handleClose("route_change");
  }, [pathname, handleClose]);

  useEffect(() => {
    if (!isReady) return;
    const pending = getPendingPromoCampaign(Date.now());
    if (!pending) return;

    if (pending.media && pending.mode !== "embed") {
      const preload = new Image();
      preload.src = pending.media.src;
    }

    const timer = setTimeout(() => {
      if (isPromoConsumed()) return;
      markPromoConsumed();
      campaignRef.current = pending;
      setCampaign(pending);
      trackPromoEvent("promo_impression", { promo_id: pending.id });
    }, pending.showDelayMs ?? DEFAULT_PROMO_SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isReady]);

  if (!campaign) return null;

  return <PromoDialog campaign={campaign} onClose={handleClose} />;
};

type PromoDialogProps = {
  campaign: PromoCampaign;
  onClose: (reason: PromoCloseReason) => void;
};

/** Presents a campaign as an accessible dialog with an optional auto-close countdown. */
export const PromoDialog: FC<PromoDialogProps> = ({ campaign, onClose }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const durationMs = getDisplayDurationMs(campaign);
  const remainingMsRef = useRef<number>(durationMs ?? 0);

  const [isHovered, setIsHovered] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const isPaused = isHovered || isTouching || isFocusWithin;

  const showMedia = campaign.mode !== "embed" && Boolean(campaign.media);
  const showEmbed = campaign.mode !== "media" && Boolean(campaign.embedUrl);

  useEffect(() => {
    if (durationMs === null || isPaused) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => onClose("timer"), remainingMsRef.current);
    return () => {
      clearTimeout(timer);
      remainingMsRef.current = Math.max(0, remainingMsRef.current - (Date.now() - startedAt));
    };
  }, [durationMs, isPaused, onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
      if (previouslyFocused && previouslyFocused !== body && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose("escape");
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    setIsFocusWithin(event.target !== dialogRef.current);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (!next || next === dialogRef.current || !dialogRef.current?.contains(next)) {
      setIsFocusWithin(false);
    }
  };

  const handlePointerEnter = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") setIsHovered(true);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") setIsHovered(false);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") setIsTouching(true);
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") setIsTouching(false);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-[20px] sm:p-[32px]"
      data-testid="promo-overlay"
    >
      <div
        aria-hidden="true"
        className="promo-scrim absolute inset-0 bg-primaryDark-0/55 backdrop-blur-[6px]"
        data-testid="promo-scrim"
        onClick={() => onClose("scrim")}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="promo-card relative w-full max-w-[480px] max-h-[min(92vh,860px)] overflow-y-auto overscroll-contain rounded-[16px] bg-text-1000 shadow-2xl shadow-primaryDark-0/30 outline-none"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <h2 id={titleId} className="sr-only">
          {campaign.title}
        </h2>

        {durationMs !== null && (
          <div
            className="promo-countdown-track absolute inset-x-0 top-0 z-20 h-[3px] overflow-hidden bg-primaryDark-400/20"
            aria-hidden="true"
          >
            <div
              className="promo-countdown h-full bg-primaryDark-400"
              data-testid="promo-countdown"
              style={{
                animationDuration: `${durationMs}ms`,
                animationPlayState: isPaused ? "paused" : "running",
              }}
            />
          </div>
        )}

        <button
          type="button"
          aria-label="Close announcement"
          className="absolute right-[12px] top-[12px] z-30 flex h-[40px] w-[40px] items-center justify-center rounded-full bg-text-1000/90 text-primaryDark-0 shadow-md shadow-primaryDark-0/15 backdrop-blur-sm transition-colors hover:bg-text-1000 hover:text-primaryDark-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryDark-400 focus-visible:ring-offset-2"
          onClick={() => onClose("close_button")}
        >
          <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {showMedia && campaign.media && (
          <div className="bg-[#FAF7F2]">
            <img
              src={campaign.media.src}
              alt={campaign.media.alt}
              width={1024}
              height={1024}
              decoding="async"
              className="mx-auto block h-auto w-full max-h-[62vh] object-contain"
            />
          </div>
        )}

        {showEmbed && campaign.embedUrl && (
          <iframe
            src={campaign.embedUrl}
            title={campaign.embedTitle ?? campaign.title}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            className={`block w-full min-h-[420px] border-0 ${showMedia ? "border-t border-[#E9E9E9]" : ""}`}
          />
        )}

        <div className="border-t border-[#E9E9E9] px-[20px] pb-[20px] pt-[16px] sm:px-[24px] sm:pb-[24px]">
          {campaign.eyebrow && (
            <p className="mb-[12px] text-center text-[12px] font-semibold uppercase tracking-[0.14em] text-primaryDark-300 font-open-sans">
              {campaign.eyebrow}
            </p>
          )}

          {campaign.cta && (
            <a
              href={campaign.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-[8px] rounded-[8px] border border-primaryDark-0 bg-primaryDark-0 px-[24px] py-[14px] text-[15px] font-semibold text-text-1000 font-raleway transition-colors hover:border-primaryDark-400 hover:bg-primaryDark-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryDark-400 focus-visible:ring-offset-2"
              onClick={() => onClose("cta")}
            >
              {campaign.cta.label}
              <svg viewBox="0 0 24 24" className="h-[16px] w-[16px]" fill="none" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </a>
          )}

          <button
            type="button"
            className="mt-[8px] w-full rounded-[8px] py-[10px] text-[14px] font-semibold text-text-300 font-open-sans transition-colors hover:text-primaryDark-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryDark-400"
            onClick={() => onClose("not_now")}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoModal;
